import { db } from '../../db/indexeddb'
import {
  getCloudProjects,
  getCloudProjectIds,
  getCloudProjectsByIds,
  getDeletedCloudProjects,
  createCloudProject,
  updateCloudProject,
  deleteCloudProject,
} from '../../supabase/queries'
import { CloudProjectSchema, CloudProjectPayloadSchema } from '../sync-schemas'
import { resolveCloudProjectId, resolveLocalProjectId } from '../sync-resolve'
import type { PullDescriptor } from '../sync-pull'
import type { PushDescriptor } from '../sync-push'
import type { Project } from '../../db/models'

/* ── Project ─────────────────────────────────────────── */

type ParentRef = { id: string; local_id?: string | null; parent_project_id?: string | null; parent_local_id?: string | null }

/**
 * Das lokale Elternprojekt eines Unterprojekts (#185). `undefined`: keins genannt;
 * `null`: genannt, aber (noch) nicht auf diesem Gerät. Die Gerätekennung zuerst — auf dem
 * Gerät, das beide angelegt hat, kennt der Pull sie, bevor die Cloud-Kennung zurück ist.
 */
async function resolveLocalParent(remote: { parent_project_id?: string | null; parent_local_id?: string | null }): Promise<string | null | undefined> {
  if (!remote.parent_project_id && !remote.parent_local_id) return undefined
  if (remote.parent_local_id && await db.projects.get(remote.parent_local_id)) return remote.parent_local_id
  return resolveLocalProjectId(remote.parent_project_id)
}

/** Eltern vor Kind, beliebig tief — wie bei den Aufgaben (`task.ts`, #181). */
function parentsFirst<T extends ParentRef>(records: readonly T[]): T[] {
  const byId = new Map(records.map(r => [r.id, r]))
  const byLocalId = new Map(records.flatMap(r => (r.local_id ? [[r.local_id, r] as const] : [])))
  const parentOf = (r: T) => (r.parent_project_id ? byId.get(r.parent_project_id) : undefined)
    ?? (r.parent_local_id ? byLocalId.get(r.parent_local_id) : undefined)
  const depth = new Map<T, number>()
  for (const record of records) {
    let d = 0
    const seen = new Set([record])
    for (let p = parentOf(record); p && !seen.has(p); p = parentOf(p)) { d++; seen.add(p) }
    depth.set(record, d)
  }
  return [...records].sort((a, b) => depth.get(a)! - depth.get(b)!)
}

/**
 * Schlösse `parentId` als Elternprojekt von `id` einen Zyklus? Zwei Geräte rücken über Kreuz
 * ein — hier B unter A, dort A unter B. Der eigene Stand bleibt dann stehen.
 */
async function closesLoop(id: string | undefined, parentId: string | null | undefined): Promise<boolean> {
  if (!id || !parentId) return false
  const seen = new Set<string>()
  for (let current: string | null | undefined = parentId; current && !seen.has(current); current = (await db.projects.get(current))?.parentId) {
    if (current === id) return true
    seen.add(current)
  }
  return false
}

/** Ein Unterprojekt wartet auf die Cloud-Kennung seines Elternprojekts — wie Unteraufgaben. */
async function parentPending(project: Project): Promise<boolean> {
  return project.parentId != null && !(await resolveCloudProjectId(project.parentId))
}

export const projectPullDescriptor: PullDescriptor<any> = {
  entityName: 'project',
  settingsKey: 'lastProjectSyncTime',

  fetchRemote: (userId, since) => getCloudProjects(userId, since),
  fetchRemoteIds: (userId) => getCloudProjectIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudProjectsByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudProjects(userId, since),

  safeParse: (raw) => CloudProjectSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.projects,
  getAllLocal: () => db.projects.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.projects.filter(p => !p.deletedAt).count()
    return count === 0
  },

  /*
    **Die Kommentare gehen mit** (#184): Schreibt ein Gerät einen Kommentar, während ein anderes das
    Projekt löscht, kennt dessen Kaskade ihn nicht. Anders als bei der Aufgabe (`task.ts`) ist das
    hier sicher: Projekt-Kommentare wandern nie zu einem anderen Projekt, das Löschen kann also nicht
    vor einem Umhängen ankommen.
  */
  afterRemoteDelete: async (localId) => {
    await db.deleteCommentsOfProject(localId)
    /*
      **Die Unterprojekte gehen mit** (#185, Checkliste 23): Ein Gerät legt ein Unterprojekt an,
      während ein anderes das Elternprojekt samt Ast löscht — dessen Kaskade kennt es nicht.
      Wer vorher herausgelöst wurde, hat hier schon ein anderes Elternprojekt: Der Pull legt
      die Updates (Schritt 6) vor die Löschungen (Schritt 7).
    */
    await db.deleteSubprojectsOf(localId)
  },

  // Elternprojekte zuerst: Ein Kind im selben Durchgang findet sein Elternprojekt dann schon vor.
  orderRemote: (records) => parentsFirst(records),

  /*
    Anders als eine Unteraufgabe wird ein Unterprojekt ohne auffindbares Elternprojekt NICHT
    übersprungen: Es steht dann oben im Lebensbereich (`flattenProjectTree`) — ein Projekt samt
    Aufgaben darf nicht verschwinden.
  */
  buildLocalInsert: async (remote, userId) => ({
    id: crypto.randomUUID(),
    name: remote.name,
    description: remote.description ?? null,
    status: remote.status,
    lifeSpace: remote.life_space,
    startDate: remote.start_date ? new Date(remote.start_date) : null,
    endDate: remote.end_date ? new Date(remote.end_date) : null,
    progress: remote.progress,
    order: remote.order,
    userId,
    cloudId: remote.id,
    isSystem: remote.is_system ?? false,
    systemKind: remote.system_kind ?? null,
    canvasPanelState: remote.canvas_panel_state ?? null,
    canvasSplitRatio: remote.canvas_split_ratio ?? null,
    icon: remote.icon ?? null,
    iconColor: remote.icon_color ?? null,
    nameHtml: remote.name_html ?? null,
    importedFrom: remote.imported_from ?? null,
    parentId: (await resolveLocalParent(remote)) ?? null,
    favoriteOrder: remote.favorite_order ?? null,
    createdAt: new Date(remote.created_at),
    updatedAt: new Date(remote.updated_at),
    lastModified: new Date(remote.updated_at),
    synced: true,
    syncedAt: new Date(),
    deletedAt: null,
  }),

  buildLocalUpdate: async (remote, existing) => {
    const parentId = await resolveLocalParent(remote)
    const loops = await closesLoop(existing?.id, parentId)
    return {
      name: remote.name,
      description: remote.description ?? null,
      status: remote.status,
      /*
        Lebensbereich und Zeitraum kamen bis #185 nicht an (#220 nahm an, sie täten es): Ein
        Umzug in einen anderen Lebensbereich blieb auf den anderen Geräten stehen — mit
        Unterprojekten stünde das Kind dort in einem anderen Bereich als sein Elternprojekt.
      */
      ...('life_space' in remote && remote.life_space ? { lifeSpace: remote.life_space } : {}),
      ...('start_date' in remote ? { startDate: remote.start_date ? new Date(remote.start_date) : null } : {}),
      ...('end_date' in remote ? { endDate: remote.end_date ? new Date(remote.end_date) : null } : {}),
      order: remote.order,
      isSystem: remote.is_system ?? false,
      systemKind: remote.system_kind ?? null,
      canvasPanelState: remote.canvas_panel_state ?? null,
      canvasSplitRatio: remote.canvas_split_ratio ?? null,
      icon: remote.icon ?? null,
      iconColor: remote.icon_color ?? null,
      nameHtml: remote.name_html ?? null,
      ...('imported_from' in remote ? { importedFrom: remote.imported_from ?? null } : {}),
      // Ein unauflösbares Elternprojekt lässt den lokalen Wert stehen, ebenso eins, das einen
      // Zyklus schlösse. Fehlen die Spalten (Cloud ohne Migration), bleibt er auch.
      ...(parentId !== null && !loops && ('parent_project_id' in remote || 'parent_local_id' in remote) ? { parentId: parentId ?? null } : {}),
      // Fehlt die Spalte (Cloud ohne Migration), bleibt der Stern hier stehen (#210).
      ...('favorite_order' in remote ? { favoriteOrder: remote.favorite_order ?? null } : {}),
      updatedAt: new Date(remote.updated_at),
    }
  },
}

export const projectPushDescriptor: PushDescriptor<Project> = {
  entityName: 'CloudProject',
  getLocal: (id) => db.getProject(id),
  getLocalCloudId: (p) => p.cloudId,
  getLocalId: (p) => p.id,
  table: db.projects,

  canCreate: async (project) => ({ defer: await parentPending(project) }),
  canUpdate: async (project) => ({ defer: await parentPending(project) }),

  buildCreatePayload: async (project) => {
    const payload: Record<string, any> = CloudProjectPayloadSchema.parse({
      name: project.name,
      description: project.description,
      status: project.status,
      life_space: project.lifeSpace,
      start_date: project.startDate?.toISOString() ?? null,
      end_date: project.endDate?.toISOString() ?? null,
      progress: project.progress,
      order: project.order,
      local_id: project.id,
      is_system: project.isSystem ?? false,
      system_kind: project.systemKind ?? null,
      canvas_panel_state: project.canvasPanelState ?? null,
      canvas_split_ratio: project.canvasSplitRatio ?? null,
      icon: project.icon ?? null,
      icon_color: project.iconColor ?? null,
      name_html: project.nameHtml ?? null,
      imported_from: project.importedFrom ?? null,
      parent_project_id: await resolveCloudProjectId(project.parentId),
      parent_local_id: project.parentId ?? null,
      favorite_order: project.favoriteOrder ?? null,
    })
    // Ohne Elternprojekt / Stern nicht mitsenden: Ein neues Projekt kommt so auch in einer Cloud
    // an, der die Spalten noch fehlen.
    if (payload.parent_project_id == null) delete payload.parent_project_id
    if (payload.parent_local_id == null) delete payload.parent_local_id
    if (payload.favorite_order == null) delete payload.favorite_order
    return payload
  },

  buildUpdatePayload: async (project) => {
    const payload: Record<string, any> = CloudProjectPayloadSchema.parse({
      name: project.name,
      description: project.description,
      status: project.status,
      life_space: project.lifeSpace,
      start_date: project.startDate?.toISOString() ?? null,
      end_date: project.endDate?.toISOString() ?? null,
      progress: project.progress,
      order: project.order,
      local_id: project.id,
      is_system: project.isSystem ?? false,
      system_kind: project.systemKind ?? null,
      // Nullable — always send explicitly so clearing the panel state/ratio
      // propagates (sync checklist point 17), same reasoning as deleted_at below.
      canvas_panel_state: project.canvasPanelState ?? null,
      canvas_split_ratio: project.canvasSplitRatio ?? null,
      icon: project.icon ?? null,
      icon_color: project.iconColor ?? null,
      name_html: project.nameHtml ?? null,
      imported_from: project.importedFrom ?? null,
      // Immer mit, auch als null: Herauslösen macht aus einem Unterprojekt wieder ein
      // Projekt oben, und das muss in der Cloud ankommen (Checkliste 17).
      parent_project_id: await resolveCloudProjectId(project.parentId),
      parent_local_id: project.parentId ?? null,
      // Immer mit, auch als null: „Aus Favoriten entfernen“ muss ankommen (#210).
      favorite_order: project.favoriteOrder ?? null,
    })
    if (!project.deletedAt) payload.deleted_at = null
    return payload
  },

  createCloud: (userId, payload) => createCloudProject(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudProject(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudProject(cloudId, userId),
}
